import { useEffect, useMemo } from 'react';

import { useMaplibreMap } from '@App/components/MaplibreMap/maplibre-map.context';
import { removeLayerAndSource } from '@App/components/MaplibreMap/maplibre-map.utils';
import { useCommunities } from '@services/query.service';

import { getLayerConfig, getFeatureColor } from '../scenarios.utils';

const SOURCE_ID = 'scenarios-communities';
const FILL_LAYER_ID = 'scenarios-communities-fill';

function ScenariosCommunitiesLayer({ selectedLayer }) {
  const { map, isLoading } = useMaplibreMap();
  const { data: communities } = useCommunities();

  const layerConfig = getLayerConfig(selectedLayer);

  const coloredFeatures = useMemo(() => {
    if (!communities?.features || !layerConfig) return null;

    const { property, stops } = layerConfig;

    return {
      type: 'FeatureCollection',
      features: communities.features.map((f) => {
        const value = f.properties?.[property] ?? 0;
        const color = getFeatureColor(value, stops);
        return {
          ...f,
          properties: { ...f.properties, fillColor: color },
        };
      }),
    };
  }, [communities, layerConfig]);

  useEffect(() => {
    if (!map || isLoading) return undefined;

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    map.addLayer({
      id: FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': ['get', 'fillColor'],
        'fill-opacity': 0.75,
      },
    }, 'z-index-3');

    return () => {
      removeLayerAndSource(map, FILL_LAYER_ID, SOURCE_ID);
    };
  }, [isLoading, map]);

  useEffect(() => {
    if (isLoading || !coloredFeatures || !map?.getSource(SOURCE_ID)) return;
    map.getSource(SOURCE_ID).setData(coloredFeatures);
  }, [isLoading, coloredFeatures, map]);

  return null;
}

export default ScenariosCommunitiesLayer;
